import { Spin, Switch, Tooltip } from "antd";
import classNames from "classnames";
import { findLast, isEmpty, isNil, max } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiChevronLeft, BiChevronRight, BiMinus, BiPlus } from "react-icons/bi";
import useCssVariables from "shared/hooks/useCssVariables";
import { NAME_SPACES } from "shared/locales/constants";
import { PdfPage, usePdfDocument } from "./pdf-pages";
import { compareReading } from "./zone-sections";

const ZOOMS = [50, 75, 100, 125, 150, 200, 300];
const CSS_UNITS = 96 / 72;
const STAGE_PADDING = 24;
const FIT_MARGIN = 2;
const BOX_PADDING = 2;
const MIN_SCALE = 0.2;
const PAGE_LINE = 1 / 3;
const SCROLL_MARGIN = 16;
const CONTROLS_HEIGHT = 64;
const VISIBLE_SHARE = 0.5;
const GHOST_RATIO = 0.62;
const GHOST_MIN = 8;
const GHOST_MAX = 14;

const boxesByPage = (boxes) => {
  const pages = {};
  for (const [zoneId, zoneBoxes] of Object.entries(boxes || {}))
    for (const box of zoneBoxes)
      pages[box.page] = [...(pages[box.page] || []), { zoneId, box }];
  return pages;
};

const boxKey = (zoneId, box) =>
  `${zoneId}-${box.marker ?? box.left}-${box.line ?? box.top}`;

const isVisible = (node, stage) => {
  const box = node.getBoundingClientRect();
  const view = stage.getBoundingClientRect();
  const width =
    Math.min(box.right, view.right - SCROLL_MARGIN) -
    Math.max(box.left, view.left + SCROLL_MARGIN);
  const height =
    Math.min(box.bottom, view.bottom - CONTROLS_HEIGHT) -
    Math.max(box.top, view.top + SCROLL_MARGIN);
  const area = Math.max(0, width) * Math.max(0, height);
  return area >= VISIBLE_SHARE * box.width * box.height;
};

const ghostSize = (box, scale) =>
  Math.min(GHOST_MAX, Math.max(GHOST_MIN, box.height * scale * GHOST_RATIO));

const relativeTop = (node, stage) =>
  node.getBoundingClientRect().top -
  stage.getBoundingClientRect().top +
  stage.scrollTop;

const relativeLeft = (node, stage) =>
  node.getBoundingClientRect().left -
  stage.getBoundingClientRect().left +
  stage.scrollLeft;

const ZoneBox = ({
  zoneId,
  box,
  scale,
  info,
  selected,
  active,
  ghost,
  showZones,
  boxRef,
  onHover,
  onClick,
}) => {
  const nodeRef = useRef(null);
  useCssVariables(nodeRef, {
    "--box-left": box.left * scale - BOX_PADDING,
    "--box-top": box.top * scale - BOX_PADDING,
    "--box-width": box.width * scale + 2 * BOX_PADDING,
    "--box-height": box.height * scale + 2 * BOX_PADDING,
    "--ghost-size": ghostSize(box, scale),
  });
  const setNode = useCallback(
    (node) => {
      nodeRef.current = node;
      boxRef(node);
    },
    [boxRef],
  );
  return (
    <Tooltip
      title={
        <span className="dynamic-documents-editor__box-tip">
          <strong>{info.title}</strong>
          <span>{info.tip}</span>
        </span>
      }
      mouseEnterDelay={0.05}
      destroyTooltipOnHide
      rootClassName="dynamic-documents-editor__box-tooltip"
    >
      <button
        type="button"
        ref={setNode}
        aria-label={info.label}
        className={classNames(
          "dynamic-documents-editor__box",
          `dynamic-documents-editor__box--${info.status}`,
          {
            "dynamic-documents-editor__box--shown": showZones,
            "dynamic-documents-editor__box--hover": active,
            "dynamic-documents-editor__box--selected": selected,
          },
        )}
        onMouseEnter={() => onHover(zoneId)}
        onMouseLeave={() => onHover(null)}
        onClick={() => onClick(zoneId)}
      >
        {!isNil(ghost) && (
          <span className="dynamic-documents-editor__ghost">{ghost}</span>
        )}
      </button>
    </Tooltip>
  );
};

const PreviewPane = ({
  picker,
  preview,
  zoneSource,
  showsSource,
  boxes,
  zoneCount,
  zoneInfo,
  activeIds,
  selectedZoneId,
  focus,
  ghostOf,
  onZoneHover,
  onZoneClick,
}) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const PREVIEW = t("PREVIEW", { returnObjects: true });
  const stageRef = useRef(null);
  const pageNodes = useRef([]);
  const boxNodes = useRef(new Map());
  const boxRefs = useRef(new Map());
  const [stageWidth, setStageWidth] = useState(0);
  const [zoom, setZoom] = useState(null);
  const [page, setPage] = useState(1);
  const [showZones, setShowZones] = useState(false);

  const sourceBytes = zoneSource.source?.bytes ?? null;
  const fallback =
    !showsSource &&
    isNil(preview.bytes) &&
    preview.failed &&
    !isNil(sourceBytes);
  const bytes = showsSource || fallback ? sourceBytes : preview.bytes;
  const active = useMemo(() => new Set(activeIds), [activeIds]);
  const { pdf, sizes, failed: unreadable } = usePdfDocument(bytes);
  const pages = sizes.length;
  const widest = max(sizes.map(({ width }) => width)) ?? 0;
  const fitScale =
    widest > 0 && stageWidth > 0
      ? Math.max(
          MIN_SCALE,
          (stageWidth - 2 * STAGE_PADDING - FIT_MARGIN) / widest,
        )
      : 1;
  const scale = isNil(zoom) ? fitScale : (zoom / 100) * CSS_UNITS;
  const percent = Math.round((scale / CSS_UNITS) * 100);
  const byPage = useMemo(() => boxesByPage(boxes), [boxes]);

  useEffect(() => {
    const stage = stageRef.current;
    if (isNil(stage) || isNil(window.ResizeObserver)) return undefined;
    const observer = new ResizeObserver(([entry]) =>
      setStageWidth(entry.contentRect.width),
    );
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (pages > 0 && page > pages) setPage(pages);
  }, [pages, page]);

  useEffect(() => {
    const stage = stageRef.current;
    const nodes = isNil(focus) ? null : boxNodes.current.get(focus.zoneId);
    if (isNil(stage) || isEmpty(nodes)) return;
    const placed = [...nodes.values()];
    if (placed.some(({ node: shown }) => isVisible(shown, stage))) return;
    const [{ node }] = placed.sort((a, b) => compareReading(a.box, b.box));
    const rect = node.getBoundingClientRect();
    stage.scrollTo({
      top: Math.max(
        0,
        relativeTop(node, stage) - stage.clientHeight / 2 + rect.height / 2,
      ),
      left: Math.max(
        0,
        relativeLeft(node, stage) - stage.clientWidth / 2 + rect.width / 2,
      ),
      behavior: "smooth",
    });
  }, [focus]);

  const trackPage = () => {
    const stage = stageRef.current;
    if (isNil(stage)) return;
    const line = stage.scrollTop + stage.clientHeight * PAGE_LINE;
    const index = pageNodes.current
      .slice(0, pages)
      .reduce(
        (current, node, position) =>
          !isNil(node) && relativeTop(node, stage) <= line ? position : current,
        0,
      );
    setPage(index + 1);
  };

  const goToPage = (number) => {
    const stage = stageRef.current;
    const node = pageNodes.current[number - 1];
    if (isNil(stage) || isNil(node)) return;
    stage.scrollTo({
      top: Math.max(0, relativeTop(node, stage) - SCROLL_MARGIN),
      behavior: "smooth",
    });
    setPage(number);
  };

  const zoomIn = () => setZoom(ZOOMS.find((value) => value > percent) ?? zoom);
  const zoomOut = () =>
    setZoom(findLast(ZOOMS, (value) => value < percent) ?? zoom);

  const registerBox = (zoneId, key, box) => {
    const cached = boxRefs.current.get(key);
    if (cached?.box === box) return cached.ref;
    const ref = (node) => {
      const nodes = boxNodes.current.get(zoneId) ?? new Map();
      if (isNil(node)) nodes.delete(key);
      else nodes.set(key, { node, box });
      if (nodes.size === 0) boxNodes.current.delete(zoneId);
      else boxNodes.current.set(zoneId, nodes);
    };
    boxRefs.current.set(key, { box, ref });
    return ref;
  };

  const emptyText = () => {
    if (showsSource)
      return zoneSource.failed ? PREVIEW.SOURCE_FAILED : PREVIEW.SOURCE_LOADING;
    return preview.failed ? PREVIEW.FAILED : PREVIEW.WAITING;
  };

  const ready = !isNil(pdf) && pages > 0 && !isNil(bytes);

  return (
    <section className="dynamic-documents-editor__preview">
      <div className="dynamic-documents-editor__preview-bar">
        {picker}
        {!showsSource && preview.loading && (
          <span className="dynamic-documents-editor__muted">
            <Spin size="small" /> {PREVIEW.UPDATING}
          </span>
        )}
        {!showsSource &&
          !fallback &&
          !preview.loading &&
          preview.failed &&
          ready && (
            <span className="dynamic-documents-editor__error">
              {PREVIEW.FAILED}
            </span>
          )}
        <label className="dynamic-documents-editor__show-zones">
          <Switch size="small" checked={showZones} onChange={setShowZones} />
          {PREVIEW.SHOW_ZONES}
        </label>
      </div>
      {showsSource && (
        <div className="dynamic-documents-editor__notice">
          {PREVIEW.PENDING_FILE}
        </div>
      )}
      {fallback && (
        <div className="dynamic-documents-editor__notice">
          {PREVIEW.SOURCE_FALLBACK}
        </div>
      )}
      <div className="dynamic-documents-editor__preview-body">
        <div
          ref={stageRef}
          className="dynamic-documents-editor__stage"
          onScroll={trackPage}
        >
          {ready ? (
            <div className="dynamic-documents-editor__pages">
              {sizes.map((size, index) => (
                <PdfPage
                  key={index}
                  ref={(node) => {
                    pageNodes.current[index] = node;
                  }}
                  pdf={pdf}
                  scrollRoot={stageRef}
                  number={index + 1}
                  size={size}
                  scale={scale}
                >
                  {(byPage[index] || []).map(({ zoneId, box }) => {
                    const key = boxKey(zoneId, box);
                    const isActive = active.has(zoneId);
                    return (
                      <ZoneBox
                        key={key}
                        zoneId={zoneId}
                        box={box}
                        scale={scale}
                        info={zoneInfo(zoneId)}
                        selected={zoneId === selectedZoneId}
                        active={isActive}
                        ghost={
                          isActive && (box.line ?? 0) === 0
                            ? ghostOf(zoneId, box)
                            : null
                        }
                        showZones={showZones}
                        boxRef={registerBox(zoneId, key, box)}
                        onHover={onZoneHover}
                        onClick={onZoneClick}
                      />
                    );
                  })}
                </PdfPage>
              ))}
            </div>
          ) : (
            <div className="dynamic-documents-editor__preview-empty">
              {unreadable ? PREVIEW.UNREADABLE : emptyText()}
            </div>
          )}
        </div>
        {ready && (
          <div className="dynamic-documents-editor__preview-controls">
            <button
              type="button"
              aria-label={PREVIEW.PREVIOUS_PAGE}
              title={PREVIEW.PREVIOUS_PAGE}
              disabled={page <= 1}
              onClick={() => goToPage(page - 1)}
            >
              <BiChevronLeft />
            </button>
            <span>{t("PREVIEW.PAGE_OF", { page, pages })}</span>
            <button
              type="button"
              aria-label={PREVIEW.NEXT_PAGE}
              title={PREVIEW.NEXT_PAGE}
              disabled={page >= pages}
              onClick={() => goToPage(page + 1)}
            >
              <BiChevronRight />
            </button>
            <span className="dynamic-documents-editor__preview-divider" />
            <button
              type="button"
              aria-label={PREVIEW.ZOOM_OUT}
              title={PREVIEW.ZOOM_OUT}
              disabled={percent <= ZOOMS[0]}
              onClick={zoomOut}
            >
              <BiMinus />
            </button>
            <button
              type="button"
              className="dynamic-documents-editor__preview-zoom"
              title={PREVIEW.ZOOM_FIT_HINT}
              onClick={() => setZoom(null)}
            >
              {isNil(zoom) ? PREVIEW.ZOOM_FIT : t("PREVIEW.ZOOM", { zoom })}
            </button>
            <button
              type="button"
              aria-label={PREVIEW.ZOOM_IN}
              title={PREVIEW.ZOOM_IN}
              disabled={percent >= ZOOMS[ZOOMS.length - 1]}
              onClick={zoomIn}
            >
              <BiPlus />
            </button>
          </div>
        )}
        {ready && zoneCount > 0 && isEmpty(boxes) && !zoneSource.loading && (
          <span className="dynamic-documents-editor__preview-hint">
            {zoneSource.failed ? PREVIEW.ZONES_FAILED : PREVIEW.NO_BOXES}
          </span>
        )}
      </div>
    </section>
  );
};

export default PreviewPane;
